import { call, put, select, takeEvery } from 'redux-saga/effects'
import { testFeature } from '@opallabs/principles'
import actionCreators from '../../actions/fetchAllAssignments'
import { AssignmentsActions } from '../../actions/fetchAllAssignments/AssignmentsActions'
import { fetchMoments } from '../../actions/getMoments/fetchMoments'
import { loadJsonApiEntities } from '../../actions/loadJsonApiEntities'
import addViewToStore from '../../actions/views/addViewToStore'
import { fetchAssignments } from '../../actions/workflow/fetchAssignments'
import { getMomentIdsFromAssignmentsResponse } from './getMomentIdsFromAssignmentsResponse'
import type {
  ActionWithPayload,
  Assignment,
  AssignmentsResponse,
  FetchAllAssignmentsSaga,
  FetchAssignments,
  FetchMoments,
  MomentsResponse,
  Next,
  Return,
  State,
  Yield,
} from './types'

export const _fetchAssignments: FetchAssignments = (params) => {
  if (testFeature('workflow_on_moments')) {
    return fetchAssignments(params)
  }
  return Promise.resolve(null)
}

export const _fetchMoments: FetchMoments = (params) => {
  if (testFeature('workflow_on_moments')) {
    return fetchMoments(params)
  }
  return Promise.resolve(null)
}

const isAssignments = (
  response: AssignmentsResponse | MomentsResponse
): response is AssignmentsResponse =>
  response.data.data.length > 0 && response.data.data[0].type === 'assignment'

export function* fetchAllAssignments(
  action: ActionWithPayload
): Generator<Yield, Return, Next> {
  try {
    const view = yield select(
      (state: State) =>
        state.entityDataByView &&
        action.payload.view &&
        state.entityDataByView[action.payload.view]
    )

    if (view === undefined) {
      yield put(addViewToStore({ view: action.payload.view }))
    }

    const assignmentsResponse = yield call(
      _fetchAssignments,
      action.payload.params
    )

    let assignmentsWithNullDueDatesResponse: Next | null = null

    /*
     *
     * To filter out overdue approvals and overdue tasks for these fetches,
     * a due_date filter was added to the fetch parameters. However, adding this filter
     * also filters out any tasks/approvals with no due_date set at all.
     * Thus, we need an additional second fetch that's identical but gets the null due_date entities.
     *
     */
    if (
      action.payload.viewKey === 'approvals.pending' ||
      action.payload.viewKey === 'tasks.todo'
    ) {
      const newParams = {
        ...action.payload.params,
        filter: {
          ...action.payload.params.filter,
          due_date: 'null',
          // Undated work is a plate only while it is fresh — the same
          // 30-day rule the Assignments view and the phase-item buckets
          // apply. Items whose date comes from the moment's schedule are
          // not affected.
          undated_created_days: '30',
        },
      }
      assignmentsWithNullDueDatesResponse = yield call(
        _fetchAssignments,
        newParams
      )
    }

    if (assignmentsResponse !== null) {
      const momentIds = getMomentIdsFromAssignmentsResponse(
        assignmentsResponse as AssignmentsResponse
      )

      if (
        assignmentsWithNullDueDatesResponse !== null &&
        isAssignments(assignmentsWithNullDueDatesResponse)
      ) {
        momentIds.push(
          ...getMomentIdsFromAssignmentsResponse(
            assignmentsWithNullDueDatesResponse
          )
        )
      }

      const momentsResponse = yield call(_fetchMoments, {
        'page[limit]': 9999,
        'filter[brand_id]': action.payload.params.filter.brand_id,
        'filter[id]': momentIds.join(','),
        'expose[unscheduled_moments]': true,
        include: 'brand',
      })

      if (momentsResponse !== null) {
        const data = { ...assignmentsResponse.data } as Assignment

        if (
          assignmentsWithNullDueDatesResponse !== null &&
          isAssignments(assignmentsWithNullDueDatesResponse)
        ) {
          const newData = assignmentsWithNullDueDatesResponse.data
          const responseOneIncluded = data.included || []
          const responseTwoIncluded = newData.included || []

          data.included = [...responseOneIncluded, ...responseTwoIncluded]
          data.data = [...data.data, ...newData.data]
        }

        yield put(
          loadJsonApiEntities({
            ...data,
            view: action.payload.view,
            entityType: 'assignment',
            ...(action.payload.viewKey && { viewKey: action.payload.viewKey }),
          })
        )

        yield put(
          loadJsonApiEntities({
            ...momentsResponse.data,
          })
        )
      }
    }

    yield put(
      actionCreators.fetchAssignmentsSuccess({ view: action.payload.view })
    )
  } catch (error: unknown) {
    console.error(error)

    yield put(
      actionCreators.fetchAssignmentsError({
        error: error as Error,
        view: action.payload.view,
      })
    )
  }
}

export function* fetchAllAssignmentsSaga(): FetchAllAssignmentsSaga {
  yield takeEvery(
    AssignmentsActions.FETCH_ALL_ASSIGNMENTS_REQUEST,
    fetchAllAssignments
  )
}

export default [fetchAllAssignmentsSaga]
